import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "core/store/authStore";
import { useProviderStore } from "core/store/providerStore";
import RoleSwitcher from "shared/components/RoleSwitcher";
import {
  acceptOrder,
  declineOrder,
  listMyAssignedOrders,
} from "features/provider/services/providerOrderApi";
import { createVehicle } from "features/vehicles/services/vehicleApi";
import {
  endDriverSession,
  getMyDriverSession,
  listAssignableVehicles,
  startDriverSession,
} from "features/driver-sessions/services/driverSessionApi";
import type { VehicleType, DriversMeResponse, AssignableVehicle } from "types/api.generated";
import { ROUTES } from "core/router/routes";
import { queryKeys } from "core/query/queryKeys";
import { invalidateFleetQueries } from "core/query/invalidateFleet";
import { useActiveOfferUpdates } from "shared/hooks/realtime/useActiveOfferUpdates";
import { useDriverSessionLocation } from "shared/hooks/useDriverSessionLocation";
import { useUIStore } from "core/store/uiStore";
import OfferCard from "pages/provider/components/OfferCard";
import OrgSwitcher from "shared/components/provider/OrgSwitcher";
import type { OrderResponse } from "types/api.generated";
import type { ApiError } from "types/api.types";
import "pages/home/components/homepage/homepage.css";
import { formatVehicleType } from "features/drivers/lib/driverLicenceStatus";
import { formatDriverApiError } from "features/drivers/lib/driverErrors";
import { getMyDriverProfiles } from "features/drivers/services/driverApi";

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-amber-50 text-amber-600",
  accepted: "bg-blue-50 text-blue-600",
  picked_up: "bg-blue-50 text-blue-600",
  in_transit: "bg-blue-50 text-blue-600",
  delivered: "bg-green-50 text-green-600",
  cancelled: "bg-red-50 text-red-500",
  no_provider: "bg-slate-100 text-slate-600",
};

const VERIFICATION_BADGE: Record<string, string> = {
  unverified: "bg-slate-100 text-slate-700",
  pending: "bg-amber-50 text-amber-700",
  verified: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  expired: "bg-orange-50 text-orange-700",
};

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuthStore();
  const { activeOrgId, refreshVehicles, orgMemberships } = useProviderStore();
  const queryClient = useQueryClient();

  const profilesQuery = useQuery<DriversMeResponse>({
    queryKey: queryKeys.drivers.me,
    queryFn: getMyDriverProfiles,
  });

  const driverOrgIds = useMemo(
    () => profilesQuery.data?.profiles.map((p) => p.organizationId) ?? [],
    [profilesQuery.data],
  );

  const organizationId = useMemo(() => {
    if (activeOrgId && driverOrgIds.includes(activeOrgId)) return activeOrgId;
    if (driverOrgIds[0]) return driverOrgIds[0];
    return activeOrgId ?? orgMemberships[0]?.orgId ?? "";
  }, [activeOrgId, driverOrgIds, orgMemberships]);
  const rootRef = useRef<HTMLDivElement>(null);

  const [deliveries, setDeliveries] = useState<OrderResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingOnline, setTogglingOnline] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>("");
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [newPlate, setNewPlate] = useState("");
  const [newType, setNewType] = useState<VehicleType>("bike");
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  const assignableQuery = useQuery<AssignableVehicle[]>({
    queryKey: queryKeys.organizations.assignable(organizationId),
    queryFn: listAssignableVehicles,
    enabled: Boolean(organizationId),
  });

  const sessionQuery = useQuery({
    queryKey: queryKeys.driverSessions.me,
    queryFn: getMyDriverSession,
  });

  const assignable = useMemo(
    () => assignableQuery.data ?? [],
    [assignableQuery.data],
  );
  const session = sessionQuery.data;
  const isOnline = session?.status === "online";
  const activeAssignable =
    assignable.find(
      (v) => v.id === (session?.vehicleId ?? selectedVehicleId),
    ) ??
    assignable[0] ??
    null;

  const firstName = user?.displayName?.split(" ")[0] ?? "Provider";

  useEffect(() => {
    void refreshVehicles();
  }, [refreshVehicles]);

  useEffect(() => {
    const fromQuery = searchParams.get("vehicle");
    if (fromQuery) setSelectedVehicleId(fromQuery);
    else if (assignable[0]) setSelectedVehicleId(assignable[0].id);
  }, [searchParams, assignable]);

  useEffect(() => {
    listMyAssignedOrders({ perPage: 50 })
      .then((res) => setDeliveries(res.orders))
      .catch(() => setDeliveries([]))
      .finally(() => setLoading(false));
  }, []);

  const totalCount = deliveries.length;
  const activeCount = deliveries.filter((d) =>
    ["accepted", "picked_up", "in_transit"].includes(d.status),
  ).length;
  const completedCount = deliveries.filter(
    (d) => d.status === "delivered",
  ).length;
  const earnings = deliveries
    .filter((d) => d.status === "delivered")
    .reduce((sum, d) => sum + d.amount, 0);

  const offerQuery = useActiveOfferUpdates(isOnline);
  const offer = offerQuery.data ?? null;
  const offerLoading = offerQuery.isLoading;

  const hasActiveTransitDelivery = deliveries.some((d) =>
    ["picked_up", "in_transit"].includes(d.status),
  );

  useDriverSessionLocation(isOnline, {
    activeDelivery: hasActiveTransitDelivery,
  });

  const refreshOffer = () => void offerQuery.refetch();
  const clearOffer = () =>
    queryClient.setQueryData(queryKeys.orders.activeOffer, null);

  const handleAcceptOffer = async () => {
    if (!offer) return;
    setAccepting(true);
    try {
      await acceptOrder(offer.orderId);
      showToast("Job accepted! Head to pickup.", "success");
      clearOffer();
      navigate(ROUTES.PROVIDER.ACTIVE_DELIVERIES);
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.status === 403) showToast("Offer expired", "error");
      else if (apiErr.status === 409) showToast("Already taken", "error");
      else showToast(apiErr.message || "Could not accept offer", "error");
      clearOffer();
      void refreshOffer();
    } finally {
      setAccepting(false);
    }
  };

  const handleDeclineOffer = async () => {
    if (!offer) return;
    setDeclining(true);
    try {
      await declineOrder(offer.orderId);
      clearOffer();
      void refreshOffer();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      showToast(apiErr.message || "Could not decline offer", "error");
    } finally {
      setDeclining(false);
    }
  };

  const goOnlineMutation = useMutation({
    mutationFn: (vehicleId: string) =>
      startDriverSession({ organizationId, vehicleId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.driverSessions.me,
      });
    },
  });

  const goOfflineMutation = useMutation({
    mutationFn: endDriverSession,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.driverSessions.me,
      });
      clearOffer();
    },
  });

  const handleToggleOnline = async () => {
    if (!organizationId) return;
    if (
      driverOrgIds.length > 0 &&
      activeOrgId &&
      !driverOrgIds.includes(activeOrgId)
    ) {
      showToast(
        "Switch to an organisation where you are enabled as a driver.",
        "error",
      );
      return;
    }
    setTogglingOnline(true);
    try {
      if (isOnline) {
        await goOfflineMutation.mutateAsync();
      } else {
        const vehicleId = selectedVehicleId || activeAssignable?.id;
        const row = assignable.find((v) => v.id === vehicleId);
        if (!vehicleId || !row?.canGoOnline) return;
        await goOnlineMutation.mutateAsync(vehicleId);
      }
      await sessionQuery.refetch();
    } catch (err: unknown) {
      showToast(
        formatDriverApiError(err, "Could not update online status."),
        "error",
      );
    } finally {
      setTogglingOnline(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const addVehicleMutation = useMutation({
    mutationFn: () => {
      const orgId = activeOrgId ?? orgMemberships[0]?.orgId;
      if (!orgId || !newPlate.trim()) {
        throw new Error("Organisation and plate are required");
      }
      return createVehicle({
        organizationId: orgId,
        vehicleType: newType,
        plateNumber: newPlate.trim(),
        label: `${formatVehicleType(newType)} · ${newPlate.trim()}`,
      });
    },
    onSuccess: async (v) => {
      const orgId = activeOrgId ?? orgMemberships[0]?.orgId ?? v.organizationId;
      await invalidateFleetQueries(queryClient, {
        orgId,
        vehicleId: v.id,
      });
      await refreshVehicles({ force: true });
      setShowAddVehicle(false);
      setNewPlate("");
      navigate(ROUTES.ONBOARDING.VERIFY(v.id), { replace: true });
    },
  });

  const handleAddVehicle = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPlate.trim()) return;
    addVehicleMutation.mutate();
  };

  return (
    <div
      ref={rootRef}
      className="min-h-screen bg-background-light font-display text-slate-900 overflow-x-hidden"
    >
      <header className="sticky top-0 z-50 bg-background-light/85 backdrop-blur-xl border-b border-primary/8 px-6 lg:px-20 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="bg-primary p-1.5 rounded-xl shadow-lg shadow-primary/25">
              <span
                className="material-symbols-outlined text-white text-[22px]"
                style={{ fontVariationSettings: '"FILL" 1' }}
              >
                local_shipping
              </span>
            </div>
            <h2 className="text-[1.4rem] font-black tracking-tighter">
              Errandi
            </h2>
          </Link>
          <div className="flex items-center gap-4">
            <RoleSwitcher />
            <Link
              to="/account"
              className="hidden sm:flex items-center gap-2.5 bg-white border border-slate-100 rounded-xl px-3.5 py-2 shadow-sm"
            >
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-black text-sm">
                  {firstName[0]?.toUpperCase()}
                </span>
              </div>
              <span className="text-sm font-bold text-slate-700">
                {firstName}
              </span>
            </Link>
            <button
              onClick={handleLogout}
              className="text-sm font-bold px-4 py-2 text-slate-500 hover:text-primary flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-[18px]">
                logout
              </span>
              <span className="hidden sm:block">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <section className="px-6 lg:px-20 pt-10 pb-6">
        <div className="max-w-7xl mx-auto space-y-4">
          <h1 className="text-3xl font-black">
            Welcome back, <span className="text-primary">{firstName}</span>
          </h1>

          <OrgSwitcher className="mb-2" />

          {assignable.length === 0 && !assignableQuery.isLoading && (
            <p className="text-sm text-slate-500">
              No vehicles yet.{" "}
              <button
                type="button"
                onClick={() => navigate(ROUTES.ONBOARDING.SETUP)}
                className="text-primary font-semibold"
              >
                Complete setup
              </button>
            </p>
          )}

          {assignable.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                    Vehicle
                  </label>
                  <select
                    value={
                      isOnline ? (session?.vehicleId ?? "") : selectedVehicleId
                    }
                    disabled={isOnline}
                    onChange={(e) => setSelectedVehicleId(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  >
                    {assignable.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label || v.plateNumber || v.id}
                      </option>
                    ))}
                  </select>
                </div>
                {activeAssignable && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                      Verification
                    </label>
                    <span
                      className={`inline-block text-xs font-bold uppercase px-3 py-2 rounded-xl ${
                        VERIFICATION_BADGE[
                          activeAssignable.vehicleVerificationStatus
                        ] ?? ""
                      }`}
                    >
                      {activeAssignable.vehicleVerificationStatus}
                    </span>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                    On duty
                  </label>
                  <button
                    type="button"
                    disabled={
                      togglingOnline ||
                      (!isOnline &&
                        !assignable.find((v) => v.id === selectedVehicleId)
                          ?.canGoOnline)
                    }
                    onClick={() => void handleToggleOnline()}
                    title={
                      assignable.find((v) => v.id === selectedVehicleId)
                        ?.goOnlineBlockedReason ?? undefined
                    }
                    className={`px-5 py-2 rounded-xl text-sm font-bold transition-colors ${
                      isOnline
                        ? "bg-emerald-500 text-white"
                        : "bg-slate-100 text-slate-600"
                    } disabled:opacity-40`}
                  >
                    {togglingOnline ? "…" : isOnline ? "Online" : "Go online"}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddVehicle((s) => !s)}
                  className="text-sm font-bold text-slate-600 border border-slate-200 px-4 py-2 rounded-xl"
                >
                  + Add vehicle
                </button>
              </div>
              {(() => {
                const row = assignable.find((v) => v.id === selectedVehicleId);
                const block = row?.goOnlineBlockedReason;
                if (!block) return null;
                return (
                  <p className="text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
                    {block}{" "}
                    {block.toLowerCase().includes("licence") && (
                      <Link
                        to={ROUTES.ONBOARDING.LICENCE}
                        className="font-semibold text-primary"
                      >
                        Verify licence
                      </Link>
                    )}
                    {row?.vehicleVerificationStatus === "unverified" && (
                      <Link
                        to={ROUTES.ONBOARDING.VERIFY(row.id)}
                        className="font-semibold text-primary ml-1"
                      >
                        Submit vehicle docs
                      </Link>
                    )}
                  </p>
                );
              })()}
            </div>
          )}

          {showAddVehicle && (
            <form
              onSubmit={handleAddVehicle}
              className="bg-white rounded-2xl border border-slate-100 p-4 flex flex-wrap gap-3 items-end"
            >
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">
                  Type
                </label>
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as VehicleType)}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="bike">Bike</option>
                  <option value="tricycle">Tricycle</option>
                  <option value="car">Car</option>
                  <option value="van">Van</option>
                  <option value="truck">Truck</option>
                </select>
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-bold text-slate-500 mb-1">
                  Plate
                </label>
                <input
                  value={newPlate}
                  onChange={(e) => setNewPlate(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={addVehicleMutation.isPending}
                className="bg-primary text-white font-bold px-4 py-2 rounded-xl text-sm disabled:opacity-50"
              >
                {addVehicleMutation.isPending ? "Adding…" : "Add"}
              </button>
            </form>
          )}

          {assignable.length > 0 && (
            <div className="max-w-xl space-y-3">
              {!isOnline && (
                <p className="text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
                  Go online to receive delivery requests.
                </p>
              )}
              {isOnline && offerLoading && !offer && (
                <p className="text-sm text-slate-500">Checking for requests…</p>
              )}
              {isOnline && !offerLoading && !offer && (
                <p className="text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
                  Waiting for requests…
                </p>
              )}
              {isOnline && offer && (
                <OfferCard
                  offer={offer}
                  onAccept={() => void handleAcceptOffer()}
                  onDecline={() => void handleDeclineOffer()}
                  onExpired={() => {
                    clearOffer();
                    void refreshOffer();
                  }}
                  accepting={accepting}
                  declining={declining}
                />
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate(ROUTES.PROVIDER.ACTIVE_DELIVERIES)}
              className="bg-primary text-white font-bold px-6 py-3 rounded-xl text-sm"
            >
              Active deliveries
            </button>
            <Link
              to={ROUTES.ONBOARDING.LICENCE}
              className="border border-slate-200 font-bold px-6 py-3 rounded-xl text-sm inline-flex items-center"
            >
              Licences
            </Link>
            <Link
              to={ROUTES.PROVIDER.ORG_ADMIN}
              className="border border-slate-200 font-bold px-6 py-3 rounded-xl text-sm inline-flex items-center"
            >
              Organisation
            </Link>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-20 pb-16">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Jobs", count: totalCount, money: false },
            { label: "Active", count: activeCount, money: false },
            { label: "Completed", count: completedCount, money: false },
            { label: "Est. Earnings", count: earnings, money: true },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-white rounded-2xl p-5 border border-slate-100"
            >
              <p className="text-2xl font-black">
                {loading
                  ? "…"
                  : s.money
                    ? `₦${s.count.toLocaleString()}`
                    : s.count}
              </p>
              <p className="text-xs font-bold text-slate-500 uppercase mt-1">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 lg:px-20 pb-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-black mb-4">Recent deliveries</h2>
          {recentList(deliveries, loading, STATUS_BADGE)}
        </div>
      </section>
    </div>
  );
}

function recentList(
  deliveries: OrderResponse[],
  loading: boolean,
  badges: Record<string, string>,
) {
  const recent = deliveries.slice(0, 5);
  if (!loading && recent.length === 0) {
    return <p className="text-slate-400 text-sm">No deliveries yet.</p>;
  }
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
      {recent.map((d) => (
        <div
          key={d.id}
          className="flex justify-between px-5 py-4 border-b border-slate-50 last:border-0"
        >
          <div>
            <p className="text-sm font-bold">{d.packageDescription}</p>
            <p className="text-xs text-slate-400">{d.deliveryAddress}</p>
          </div>
          <span
            className={`text-[10px] font-black uppercase px-2 py-1 rounded-full ${badges[d.status] ?? ""}`}
          >
            {d.status.replace("_", " ")}
          </span>
        </div>
      ))}
    </div>
  );
}
